import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import {
  Flex,
  IconCheckmarkCircle,
  IconChevronRightCircle,
  IconExclamationCircle,
  IconHide,
  IconSparkle,
  Image,
  Spacer,
} from '@zillow/constellation';
import { ZillowInsuranceServicesLogo } from '@zillow/zrs-comet';
import { Heading, Paragraph, Tag } from '@zillow/constellation-10';

import { RMWContext } from '../../../../providers/RMWContext';
import { PropertiesSelectors, RoutingSelectors, UserSelectors } from '../../../../selectors';
import PropertiesActions from '../../../../actions/PropertiesActions';
import { useGetRentalProtectionStateForListingMultiPolicy } from '../../../../hooks/queries/rentalProtection';
import RentGuaranteeHome from '../../../../images/RentGuaranteeHome.svg';
import { RENT_GUARANTEE_LISTING_STATE } from '../../../../../shared/constants/RentGuaranteeConstants';
import { useGetApplicationsSummary } from '../../../../hooks/queries/summary';
import SenseOfHomeCSEvents from '../../../../model/ClickstreamEvent/RentalProtection/SenseOfHomeCSEvents';
import RentGuaranteeActiveModal from '../../../Modal/RentGuaranteeActiveModal';
import { ENTRY_POINT_PATH } from '../../../../containers/RentGuaranteePage/constants';

import {
  StyledDesktopContainerMultiPolicy,
  StyledImageContainerMultiPolicy,
  StyledPageContent,
  StyledRentGuaranteeFlexContainerMultiPolicy,
} from './StyledComponents';
import { PRODUCT_INTRO_TYPES } from '../../../../containers/RentGuaranteePage/constants';
import { StyledHidden } from '../../../rentGuarantee/StyledComponents';

export const getTextColor = (rentGuaranteeListingState, hasPremiumPaymentError = false) => {
  if (hasPremiumPaymentError) {
    return 'text.feedback.critical.hero';
  }

  switch (rentGuaranteeListingState) {
    case RENT_GUARANTEE_LISTING_STATE.PENDING_CANCELLATION:
    case RENT_GUARANTEE_LISTING_STATE.SUNSET:
      return 'text.accent.orange.hero';
    case RENT_GUARANTEE_LISTING_STATE.CANCELLED:
      return 'text.feedback.critical.hero';
    default:
      return 'text.neutral';
  }
};

export const getBannerIcon = (rentGuaranteeListingState, hasPremiumPaymentError = false) => {
  if (hasPremiumPaymentError) {
    return <IconExclamationCircle size="md" fontColor="red500" marginRight="xs" />;
  }

  switch (rentGuaranteeListingState) {
    case RENT_GUARANTEE_LISTING_STATE.PENDING_CANCELLATION:
    case RENT_GUARANTEE_LISTING_STATE.SUNSET:
      return <IconExclamationCircle size="md" fontColor="orange400" marginRight="xs" />;
    case RENT_GUARANTEE_LISTING_STATE.CANCELLED:
      return <IconHide size="md" fontColor="red500" marginRight="xs" />;
    case RENT_GUARANTEE_LISTING_STATE.ACTIVE:
      return <IconCheckmarkCircle size="md" fontColor="green400" marginRight="xs" />;
    case RENT_GUARANTEE_LISTING_STATE.EXPIRED:
      return <IconHide size="md" fontColor="red500" marginRight="xs" />;
    case RENT_GUARANTEE_LISTING_STATE.ACTIVE_RENEW:
      return <IconSparkle size="md" fontColor="brandSecondary" marginRight="xs" />;
    case RENT_GUARANTEE_LISTING_STATE.MISSING_PAYMENT_METHOD:
      return <IconExclamationCircle size="md" fontColor="yellow400" marginRight="xs" />;
    default:
      return (
        <StyledImageContainerMultiPolicy>
          <Image src={RentGuaranteeHome} loading="eager" alt="rental protection home icon" />
        </StyledImageContainerMultiPolicy>
      );
  }
};

const RentGuaranteeBannerMultiPolicy = () => {
  const {
    paths: { rentGuaranteePage, properties },
  } = useContext(RMWContext);

  const dispatch = useDispatch();

  const currentProperty = useSelector((state) => state.properties.currentProperty);
  const user = useSelector((state) => state.user);
  const listingAlias = useSelector((state) => RoutingSelectors.makeListingId(state));
  const {
    rentalProtection: { isEnabled },
  } = useSelector((state) => PropertiesSelectors.makeCurrentProperty(state));
  const userToken = useSelector((state) => UserSelectors.makeUserToken(state));

  const { data, isLoading } = useGetRentalProtectionStateForListingMultiPolicy(listingAlias, {
    enabled: Boolean(listingAlias),
  });

  const [displayEffectivePolicyScreen, setDisplayEffectivePolicyScreen] = useState(null);
  const [isBannerPopperOpen, setIsBannerPopperOpen] = React.useState(false);

  const { data: applicationsSummary, isPending: isApplicationSummaryLoading } = useGetApplicationsSummary(listingAlias);

  const getPageNavigationPath = () => {
    if (data?.rentGuaranteeListingState === RENT_GUARANTEE_LISTING_STATE.DROPPED) {
      return 'managePolicies';
    }
    if (isEnabled) {
      if (data?.rentGuaranteeListingState === RENT_GUARANTEE_LISTING_STATE.LIGHTWEIGHT_OPT_IN) {
        return 'coverageSelection';
      } else {
        return 'managePolicies';
      }
    }
    return '';
  };

  const link = `${rentGuaranteePage}/${properties}/${listingAlias}/${getPageNavigationPath()}`;
  const rentalProtectionExperimentParams = `?productIntroType=${PRODUCT_INTRO_TYPES.SOH_ILLUSTRATIVE}`;

  const isInLightweightOptInState = data?.rentGuaranteeListingState === RENT_GUARANTEE_LISTING_STATE.LIGHTWEIGHT_OPT_IN;

  useEffect(() => {
    setDisplayEffectivePolicyScreen(data?.displayEffectivePolicyScreen);
  }, [data]);

  useEffect(() => {
    if (applicationsSummary?.isApplicationsAccepted === false && isInLightweightOptInState) {
      setIsBannerPopperOpen(true);
    }
  }, [isApplicationSummaryLoading]);

  const handleBannerOnClick = () => {
    if (data?.rentGuaranteeListingState === RENT_GUARANTEE_LISTING_STATE.NOT_ENROLLED || isInLightweightOptInState) {
      SenseOfHomeCSEvents.LearnMoreBannerClick({
        currentProperty,
        user,
        entryPoint: ENTRY_POINT_PATH.DASHBOARD_OVERVIEW_PAGE,
      });
    } else {
      SenseOfHomeCSEvents.ManageRentalProtectionBannerClick({
        currentProperty,
        user,
      });
    }
  };

  const closeRentalProtectionActiveModal = () => {
    dispatch(PropertiesActions.storeEffectivePolicyScreenDisplayed({ listingAlias, userToken })).finally(() => {
      setDisplayEffectivePolicyScreen(false);
    });
  };

  if (data?.rentGuaranteeStateTitle) {
    return (
      <>
        <Link
          to={{
            pathname: link,
            state: { from: ENTRY_POINT_PATH.DASHBOARD_OVERVIEW_PAGE },
            search: !(isEnabled || data?.rentGuaranteeListingState === RENT_GUARANTEE_LISTING_STATE.DROPPED)
              ? rentalProtectionExperimentParams
              : '',
          }}
          data-testid="RentGuaranteeBannerMultiPolicy"
          onClick={handleBannerOnClick}
          data-eventid={
            data?.rentGuaranteeListingState === RENT_GUARANTEE_LISTING_STATE.NOT_ENROLLED || isInLightweightOptInState
              ? SenseOfHomeCSEvents.LearnMoreBannerClick.data_event_id
              : SenseOfHomeCSEvents.ManageRentalProtectionBannerClick.data_event_id
          }
        >
          <StyledRentGuaranteeFlexContainerMultiPolicy
            display="flex"
            flexDirection="row"
            alignItems="center"
            padding="sm"
            backgroundColor={isInLightweightOptInState ? 'blue200' : 'white'}
            hideBorder={true}
          >
            <StyledPageContent data-testid="PageContent" margin={0} padding={0}>
              <Flex display="flex" flexDirection="row" alignItems="center" justifyContent="space-between">
                <Flex display="flex" flexDirection="row" alignItems="center">
                  <Flex display="flex" flexShrink={0}>
                    {getBannerIcon(data?.rentGuaranteeListingState, data?.hasPremiumPaymentError)}
                  </Flex>
                  <Flex display="flex" flexDirection="column">
                    {data?.tagStatus && data?.tagText ? (
                      <StyledHidden breakpoint="sm_lte">
                        <ZillowInsuranceServicesLogo width="150" />
                      </StyledHidden>
                    ) : (
                      <ZillowInsuranceServicesLogo width="150" />
                    )}
                    <Flex display="flex" flexDirection={{ default: 'column-reverse', md: 'row' }}>
                      <Heading level={5} fontFamily="heading-alt" css={{ marginRight: '400' }} data-test="title">
                        {data?.rentGuaranteeStateTitle}
                      </Heading>
                      {data?.tagStatus && data?.tagText ? (
                        <Spacer marginBottom={{ default: 'xs', md: 0 }}>
                          <Tag tone={data?.tagStatus} icon={<IconSparkle />}>
                            {data?.tagText}
                          </Tag>
                        </Spacer>
                      ) : null}
                    </Flex>
                    <Paragraph
                      data-test="description"
                      css={{
                        textStyle: { base: 'body', mdDown: 'body-sm' },
                        color: getTextColor(data?.rentGuaranteeListingState, data?.hasPremiumPaymentError),
                      }}
                    >
                      {data?.rentGuaranteeStateDescription}
                    </Paragraph>
                  </Flex>
                </Flex>
                <Flex display="flex" flexDirection="row" alignItems="center" flexShrink={0}>
                  <StyledDesktopContainerMultiPolicy>
                    <Paragraph textStyle="body-bold" css={{ marginRight: '200', color: 'text.express.insight.strong' }}>
                      {data?.ctaText}
                    </Paragraph>
                  </StyledDesktopContainerMultiPolicy>
                  <IconChevronRightCircle fontColor="brand" size="sm" />
                </Flex>
              </Flex>
            </StyledPageContent>
          </StyledRentGuaranteeFlexContainerMultiPolicy>
        </Link>
        {displayEffectivePolicyScreen && (
          <RentGuaranteeActiveModal
            legalDisclaimer={data?.disclaimer}
            coverageSummaryUrl={data?.coverageSummaryUrl}
            coverageAmount={data?.coverageAmount}
            onClose={closeRentalProtectionActiveModal}
            onSubmit={closeRentalProtectionActiveModal}
          />
        )}
      </>
    );
  }

  return null;
};

export default RentGuaranteeBannerMultiPolicy;
